import { Box, useTheme } from '@chakra-ui/react';
import { Global } from '@emotion/react';
import Chart from 'react-apexcharts';
import { getTrendlineColor } from '@src/utils/color';

function TrendLineBarChart({
  data = [],
  height = 400,
  width = '100%',
  yAxisLabel = '',
  yAxisSuffix = '',
  yAxisPrefix = '',
  yAxisDecimalPoints = 0,
  yAxisLabelFormatter = null,
  tooltipFormatter = null,
  invertTrend = false,
  neutralTrend = false,
}) {
  const theme = useTheme();
  const dataCategories = data.map((d) => d.name);
  // Extract unique years and sort them chronologically
  const years = [
    ...new Set(data.flatMap((item) => item.data.map((d) => d.year))),
  ].sort((a, b) => {
    const yearA = parseInt(a.split('/')[0], 10);
    const yearB = parseInt(b.split('/')[0], 10);
    return yearA - yearB;
  }) || [];
  const xOffset = dataCategories.length === 1 ? 0.15 : 0.4;

  const widthMap = {
    1: '380px',
    2: '480px',
    3: '600px',
  };
  const containerWidth = widthMap[dataCategories.length] || width;

  const barSeries = years.map((year) => ({
    name: year,
    type: 'column',
    data: data.map((cat, idx) => {
      const entry = cat.data.find((d) => d.year === year);

      return {
        x: idx,
        y: entry?.value ?? 0,
        fillColor:
          theme.colors.onfCategory?.[cat.name]?.base || 'gray',
      };
    }),
  }));
  const trendSeries = data.map((cat) => {
    const last = cat.data.at(-1);
    const catIdx = dataCategories.indexOf(cat.name);

    return {
      name: `${cat.name}-trend`,
      type: 'line',
      color: getTrendlineColor(last.trendY1, last.trendY2, invertTrend, neutralTrend),
      data: [
        { x: catIdx - xOffset, y: last.trendY1 },
        { x: catIdx + xOffset, y: last.trendY2 },
      ],
      markers: { size: 0 },
    };
  });

  /** -----------------------------
   * Chart options
   * ----------------------------- */
  const options = {
    chart: {
      animations: { enabled: false },
      toolbar: { show: true },
    },

    title: {
      text: `Trend for ${years.at(0)}—${years.at(-1)}`,
    },

    xaxis: {
      type: 'numeric',
      min: dataCategories.length === 1 ? -0.2 : -0.5,
      max: dataCategories.length === 1 ? 0.2 : dataCategories.length - 0.5,
      tickAmount: dataCategories.length * 2,
      axisTicks: { show: false },
      labels: {
        rotate: -45,
        rotateAlways: true,
        offsetY: 20,
        formatter: (val) => {
          if (!Number.isInteger(val) || val < 0) return '';
          return dataCategories[val] ?? '';
        },
        style: { fontWeight: 'bold' },
      },
    },

    grid: {
      padding: { bottom: 85 },
    },

    yaxis: [
      {
        min: 0,
        forceNiceScale: true,
        title: { text: yAxisLabel },
        labels: {
          formatter: (value) => (yAxisLabelFormatter
            ? yAxisLabelFormatter(value)
            : `${yAxisPrefix}${value.toFixed(
              yAxisDecimalPoints,
            )}${yAxisSuffix}`),
        },
      },
    ],

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: dataCategories.length === 1 ? '200%' : '100%',
      },
    },

    stroke: {
      curve: 'straight',
      width: 4,
      colors: ['transparent'],
    },

    markers: { size: 0 },
    dataLabels: { enabled: false },
    legend: { show: false },

    tooltip: {
      shared: false,
      intersect: false,
      enabledOnSeries: barSeries.map((_s, i) => i),
      x: {
        formatter: (val, { seriesIndex, w }) => {
          const cat = dataCategories[Math.round(val)] ?? '';
          const seriesName = w.globals.seriesNames[seriesIndex];

          return tooltipFormatter
            ? tooltipFormatter({ val, cat, seriesName, w })
            : `${seriesName} – ${cat}`;
        },
      },
    },

    annotations: {
      points: dataCategories.map((_cat, idx) => ({
        x: idx,
        y: 0,
        marker: { size: 0 },
        label: {
          borderWidth: 0,
          text: `${years.at(0)}—${years.at(-1)}`,
          offsetY: 25,
          style: {
            background: 'transparent',
            color: '#666',
            fontSize: '11px',
            fontWeight: 'normal',
            fontFamily: 'Arial, sans-serif',
          },
        },
      })),
    },
  };

  return (
    <>
      {/* Scoped CSS to hide CSV export for this chart */}
      <Global
        styles={{
          '.trendline-bar-chart .exportCSV': {
            display: 'none !important',
          },
        }}
      />

      <Box
        className="trendline-bar-chart"
        width={containerWidth}
      >
        <Chart
          options={options}
          series={[...barSeries, ...trendSeries]}
          type="line"
          width={width}
          height={height}
        />
      </Box>
    </>
  );
}

export default TrendLineBarChart;
